import type { ComponentProps, ComponentType } from 'react'

import { Box, BoxProps, TextField, TextFieldProps } from '@mui/material'
import styled from 'styled-components'

import { batikalColor, separatorColor } from '../Colors'
import { textFont } from '../Fonts'
import { defaultLineHeight, defaultCalculItemHeight } from '../Metrics'
import { Text, TextDescr } from '../Native/Text'
import { View, ViewRow } from '../Native/View'

// Define the interface for the custom props
export interface CustomBoxProps {
  first?: boolean
  last?: boolean
}

//@ts-ignore
export const ViewLine = styled(Box).attrs((props: BoxProps & CustomBoxProps) => ({
  container: true,
  sx: {
    borderTopLeftRadius: props.first ? '12px' : '0px',
    borderTopRightRadius: props.first ? '12px' : '0px',
    borderBottomLeftRadius: props.last ? '12px' : '0px',
    borderBottomRightRadius: props.last ? '12px' : '0px',
    borderTop: props.first ? '1px solid #e2e8f0' : 'none',
    borderLeft: '1px solid #e2e8f0',
    borderRight: '1px solid #e2e8f0',
    borderBottom: props.last ? '1px solid #e2e8f0' : '1px solid #f1f5f9',
    boxShadow: props.last ? '0 4px 12px rgba(0,0,0,0.04)' : 'none',
    marginBottom: props.last ? '8px' : '0px',
    ...props.sx
  }
}))`
  background-color: #ffffff;
  display: inline-flex;
  min-height: ${defaultLineHeight + 6}px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: stretch;
  padding-right: 16px;
  padding-left: 16px;
  transition: background-color 0.15s ease;
  &:hover {
    background-color: #f8fafc;
  }
` as React.ComponentType<BoxProps & CustomBoxProps>

export const ViewLineResult = styled(Box).attrs((props: BoxProps & CustomBoxProps) => ({
  sx: {
    borderRadius: '12px',
    boxShadow: '0 4px 16px rgba(66, 119, 185, 0.2)',
    ...props.sx
  }
}))`
  display: flex;
  background-color: ${batikalColor};
  min-height: ${defaultLineHeight + 12}px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: stretch;
  padding-right: 16px;
  padding-left: 16px;
  margin-bottom: 8px;
  border: none;
` as React.ComponentType<BoxProps & CustomBoxProps>

export const ViewColumnLine: ComponentType<ComponentProps<typeof ViewLine>> = styled(ViewLine)`
  flex-direction: column;
` as ComponentType<ComponentProps<typeof ViewLine>>

export const ViewLineImage: ComponentType<BoxProps> = styled(Box)`
  width: 30px;
  height: 30px;
  margin-right: 8px;
  border-radius: 6px;
` as ComponentType<BoxProps>

// calculText — label a gauche
export const CalculText: ComponentType<ComponentProps<typeof Text>> = styled(Text)`
  flex-wrap: wrap;
  text-align: left;
  font-size: 14px;
  color: #475569;
` as ComponentType<ComponentProps<typeof Text>>

//calculTextRight — valeur a droite (read only)
export const CalculTextRight: ComponentType<ComponentProps<typeof Text>> = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  flex-wrap: wrap;
  text-align: right;
  margin-right: 8px;
  color: #1e293b;
` as ComponentType<ComponentProps<typeof Text>>

//calculTextSub — sous-texte (min/max)
export const SubCalculText: ComponentType<ComponentProps<typeof TextDescr>> = styled(TextDescr)`
  flex-wrap: wrap;
  text-align: left;
  font-size: 12px;
  color: #94a3b8;
` as ComponentType<ComponentProps<typeof TextDescr>>

// calculLineContainer
export const CalculTextInputContainer: ComponentType<ComponentProps<typeof View>> = styled(View)`
  flex-wrap: nowrap;
  border-radius: 8px;
  height: 34px;
  border-color: ${separatorColor};
  border-width: 0px;
  border-style: solid;
  margin-left: 4px;
` as ComponentType<ComponentProps<typeof View>>

export const CalculTextInput: ComponentType<TextFieldProps> = styled(TextField).attrs((props: TextFieldProps) => ({
  inputProps: {
    style: {
      color: '#0f172a',
      textAlign: 'right',
      fontSize: 15,
      fontWeight: '600',
      padding: '4px 8px',
      height: defaultCalculItemHeight
    },
    type: 'number',
    autoComplete: 'off'
  },
  InputProps: {
    ...props.InputProps
  }
}))`
  background-color: #f8fafc;
  border-radius: 8px;
  & .MuiOutlinedInput-root {
    border-radius: 8px;
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: #94a3b8;
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${batikalColor};
      border-width: 1.5px;
    }
  }
  & .MuiOutlinedInput-notchedOutline {
    border-color: #e2e8f0;
  }
` as ComponentType<TextFieldProps>

export const LinearGradientResult: ComponentType<BoxProps> = styled(Box)`
  flex-direction: row;
  justify-content: space-between;
  margin-left: 8px;
  margin-right: 8px;
  background-color: transparent;
  border-radius: 10px;
  min-height: 56px;
  margin-top: 3px;
  margin-bottom: 3px;
` as ComponentType<BoxProps>

export const ResultTextStyled: ComponentType<ComponentProps<typeof Text>> = styled(Text)`
  padding-left: 12px;
  font-weight: 700;
  font-size: 15px;
  color: white;
` as ComponentType<ComponentProps<typeof Text>>

export const ContainerResultText: ComponentType<ComponentProps<typeof ViewRow>> = styled(ViewRow)`
  flex-wrap: wrap;
` as ComponentType<ComponentProps<typeof ViewRow>>
export const ContainerResultValue: ComponentType<ComponentProps<typeof ViewRow>> = styled(ViewRow)`
  flex-wrap: nowrap;
  padding-right: 12px;
` as ComponentType<ComponentProps<typeof ViewRow>>

export const ResultTextValue: ComponentType<ComponentProps<typeof Text>> = styled(Text)`
  font-weight: 800;
  font-size: 26px;
  align-self: flex-end;
  padding-left: 16px;
  padding-bottom: 4px;
  flex-wrap: nowrap;
  text-align: right;
  color: white;
` as ComponentType<ComponentProps<typeof Text>>

export const ResultTextUnit: ComponentType<ComponentProps<typeof Text>> = styled(Text)`
  font-weight: 600;
  font-size: 18px;
  align-self: flex-end;
  padding-bottom: 8px;
  padding-left: 6px;
  flex-wrap: nowrap;
  text-align: right;
  color: rgba(255, 255, 255, 0.8);
` as ComponentType<ComponentProps<typeof Text>>

export const PickerViewIos: ComponentType<ComponentProps<typeof ViewRow>> = styled(ViewRow)`
  justify-content: center;
  align-items: center;
  border-bottom-width: 1px;
  border-bottom-color: ${separatorColor};
` as ComponentType<ComponentProps<typeof ViewRow>>
export const stylesLine = {
  calculTextCenter: {
    fontFamily: textFont,
    fontSize: 14,
    flexWrap: 'wrap',
    textAlign: 'center',
    color: '#475569'
  },
  iconLine: {
    width: 30,
    height: 30,
    marginRight: 0
  }
}

export const ViewDisclosure: ComponentType<ComponentProps<typeof View>> = styled(View)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
` as ComponentType<ComponentProps<typeof View>>
